import { AlertTriangle, CheckCircle2, Info, XCircle } from 'lucide-react'

import { type Gravidade } from '@viewer/lib/georef/verificacoesGeorref'
import { useVerificacoesGeorref } from '@viewer/lib/georef/useVerificacoesGeorref'

const ESTILO: Record<Gravidade, { cor: string; Icone: typeof Info; rotulo: string }> = {
  ok: { cor: 'text-[var(--color-ok-text)]', Icone: CheckCircle2, rotulo: 'Certo' },
  info: { cor: 'text-[var(--color-text-muted)]', Icone: Info, rotulo: 'Informação' },
  aviso: { cor: 'text-[var(--color-warn-text)]', Icone: AlertTriangle, rotulo: 'Aviso' },
  erro: { cor: 'text-[var(--color-error-text)]', Icone: XCircle, rotulo: 'Erro' },
}

/** As verificações da georreferenciação do modelo (verificacoesGeorref.ts):
 *  o nível LoGeoRef e o que está certo, a conferir ou errado. Só diz o que
 *  encontrou; nada aqui muda a posição. */
export default function VerificacoesGeorrefView({ modelId }: { modelId: string }) {
  const r = useVerificacoesGeorref(modelId)
  if (!r) {
    return (
      <p className="text-[11px] italic text-[var(--color-text-muted)]">
        Sem verificação: o modelo foi aberto de um .frag, sem o texto do IFC, ou antes desta versão.
      </p>
    )
  }
  const erros = r.verificacoes.filter((v) => v.gravidade === 'erro').length
  const avisos = r.verificacoes.filter((v) => v.gravidade === 'aviso').length
  return (
    <div className="space-y-1.5 text-[11px]" data-campo="verificacoes-georref">
      <div className="flex items-center gap-2">
        <span
          className="rounded border border-[var(--color-accent-border)] bg-[var(--color-accent-bg)] px-1.5 py-0.5 font-mono text-[10px] text-[var(--color-accent)]"
          title="Level of Georeferencing (Clemen e Görne, 2019): 10 morada, 20 latitude e longitude do IfcSite, 30 coordenadas na colocação do IfcSite, 40 sistema e norte no contexto, 50 IfcMapConversion com IfcProjectedCRS."
        >
          LoGeoRef {r.nivel}
        </span>
        <span className="text-[var(--color-text-muted)]">
          {erros > 0 ? `${erros} erro${erros === 1 ? '' : 's'}` : 'sem erros'}
          {avisos > 0 ? `, ${avisos} aviso${avisos === 1 ? '' : 's'}` : ''}
        </span>
      </div>
      <ul className="space-y-1">
        {r.verificacoes.map((v) => {
          const { cor, Icone, rotulo } = ESTILO[v.gravidade]
          return (
            <li key={v.chave} className="flex gap-1.5" data-gravidade={v.gravidade}>
              <Icone size={12} className={`mt-0.5 shrink-0 ${cor}`} aria-label={rotulo} />
              <span>
                <span className="font-medium text-[var(--color-text)]">{v.titulo}.</span>{' '}
                <span className="text-[var(--color-text-muted)]">{v.texto}</span>
              </span>
            </li>
          )
        })}
      </ul>
      <p className="text-[10px] leading-snug text-[var(--color-text-dim)]">
        Segundo os níveis LoGeoRef de Clemen e Görne (2019) e o artigo &quot;The Hidden Minefield of IFC Georeferencing&quot; (T.
        Krijnen e J. Gevaert, 2026).
      </p>
    </div>
  )
}
